/**
 * Libraries
 */

import { LocalForageWrapper, CachePersistor } from 'apollo3-cache-persist';
import { createUploadLink } from 'apollo-upload-client';
import localforage from 'localforage';

import { ApolloClient, from, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { logErrorMessages } from '@vue/apollo-util';

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

/*
 * Apollo
 */

import { createApolloEchoSubscriptionLink } from '@core/apollo/links/echoSubscriptionLink';
import { cache } from '@core/apollo/cache';

/**
 * Keycloak
 */

import { keycloakInstance } from '@core/keycloak';
import { updateToken } from '@core/keycloak/keycloak';

/**
 * Types
 */

import type { ErrorResponse } from '@apollo/client/link/error';
import type { ServerError } from '@apollo/client/core';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

const APOLLO_CACHE_MAX_SIZE = 10 * 1024 * 1024;
const APOLLO_CACHE_STORAGE_KEY = 'apollo-cache-persist';

localforage.config({
  name: 'lea',
  storeName: 'apollo-cache',
});

function handleDebugError(error: ErrorResponse) {
  if (import.meta.env.MODE === 'development') {
    logErrorMessages(error);
  }
}

let clientInstance: ApolloClient<any> | null = null;

/**
 * Instead of redirecting to the apollo doc for the error, we log the error in the console
 */
if (import.meta.env.MODE === 'development') {
  loadDevMessages();
  loadErrorMessages();
}

const errorLink = onError((error) => {
  const { networkError, graphQLErrors, response, operation, forward } = error;

  if (networkError) {
    handleDebugError(error);

    if (
      networkError.name === 'ServerError' &&
      (networkError as ServerError).statusCode === 401
    ) {
      updateToken().then(async (refreshedToken) => {
        if (!refreshedToken) {
          if (clientInstance) {
            await clientInstance.clearStore();
          }
          keycloakInstance.clearToken();
        } else {
          if (response) response.errors = undefined;
        }
      });
    }
  }

  if (graphQLErrors) {
    handleDebugError(error);
    for (let err of graphQLErrors) {
      if (err.extensions) {
        switch (err.extensions.code) {
          case 'UNAUTHENTICATED':
            updateToken().then((token) => {
              if (token) {
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: token,
                  },
                });
              }
              return forward(operation);
            });
        }
      }
    }
  }
});

const retryLink = new RetryLink();

const asyncAuthLink = setContext(() => ({
  headers: { authorization: `Bearer ${keycloakInstance.token}` },
}));

const httpLink = createUploadLink({
  uri: import.meta.env.VITE_APP_API_URL,
  credentials: 'include',
});

window.Pusher = Pusher;

const echoClient: Echo<'pusher'> = new Echo({
  broadcaster: 'pusher',
  key: import.meta.env.VITE_PUSHER_APP_KEY,
  wsHost: import.meta.env.VITE_PUSHER_HOST,
  wsPort: import.meta.env.VITE_PUSHER_PORT,
  forceTLS: (import.meta.env.VITE_PUSHER_SCHEME ?? 'https') === 'https',
  encrypted: (import.meta.env.VITE_PUSHER_ENCRYPTED ?? 'true') === 'true',
  disableStats: true,
  cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
  enabledTransports:
    import.meta.env.VITE_PUSHER_SCHEME === 'http' ? ['ws'] : ['ws', 'wss'],
  bearerToken: keycloakInstance.token,
  auth: {
    headers: {
      Authorization: `Bearer ${keycloakInstance.token}`,
    },
  },
  authEndpoint:
    import.meta.env.VITE_APP_BACKEND_URL + '/graphql/subscriptions/auth',
});

echoClient.connector.pusher.connection.bind('connected', () => {
  console.log('✅ WebSocket Connected!');
});

echoClient.connector.pusher.connection.bind('disconnected', () => {
  console.log('👋 WebSocket Disconnected!');
});

echoClient.connector.pusher.connection.bind('failed', () => {
  console.log('❌ WebSocket Connection Failed!');
});

const httpStack = from([errorLink, retryLink, asyncAuthLink.concat(httpLink)]);

const subscriptionLink = from([
  createApolloEchoSubscriptionLink(echoClient),
  httpStack,
]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  subscriptionLink,
  httpStack,
);

const persistor = new CachePersistor({
  cache,
  storage: new LocalForageWrapper(localforage),
  key: APOLLO_CACHE_STORAGE_KEY,
  maxSize: APOLLO_CACHE_MAX_SIZE,
});

try {
  await persistor.restore();

  console.log('✅ Apollo cache restore completed');

  if (import.meta.env.MODE === 'development') {
    persistor.getLogs(true);
  }
} catch (error) {
  console.warn('❌ Apollo cache restore failed', error);
}

const client = new ApolloClient({
  link: splitLink,
  cache,
});

clientInstance = client;

export default client;
